import React, {useRef} from 'react';
import type {SetRequired} from 'type-fest';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {TextField, type TextFieldProps} from '@netx/core-components/TextField';
import {splitEventHandler} from '@netx/core-next/utils';
import {combineRefs} from '@netx/core-next/utils/react';
import {useFormInput} from '../hooks';
import {useErrorFocus} from '../hooks/useErrorFocus';
import type {WithLegacyCommonFormFieldProps} from '../types';

export interface LegacyFormTextFieldProps
	extends WithLegacyCommonFormFieldProps<SetRequired<TextFieldProps, 'name'>> {
	changed?: boolean;
}

export const LegacyFormTextFieldPrimitive = (
	{
		changed,
		error,
		form,
		formFieldOptions,
		onChange,
		...props
	}: LegacyFormTextFieldProps,
	ref: any,
) => {
	const inputRef = useRef<HTMLInputElement>(null);

	const _error = typeof error === 'string' ? error : error?.message;

	useErrorFocus({
		error: _error,
		inputRef,
	});

	useFormInput(props.name, props.value, form, {
		required: props.required,
		...formFieldOptions,
	});

	const handleChange = useConstCallback(
		splitEventHandler(onChange, (event, value) => {
			form?.setValue(props.name, value);
		}),
	);

	return (
		<TextField
			ref={ref}
			error={_error}
			{...props}
			inputRef={combineRefs(inputRef, props.inputRef)}
			onChange={handleChange}
		/>
	);
};

/** @depracated */
export const LegacyFormTextField = React.forwardRef(
	LegacyFormTextFieldPrimitive,
);

export default LegacyFormTextField;
